<template>
  <Modal :loading="busy" title="テーブル選択" @close="onClose">
    <template #content>
      <div class="table-select">
        <div class="type">
          <SelectBox
            :items="['予約が入っているテーブルも含める', '予約可能なテーブルのみ']"
            v-model.number="selectedType"
          />
        </div>
        <ul class="table-list">
          <li class="table">
            <CheckBox small :value="-1" v-model="selectedIds" :disabled="isBlock">テーブル未指定</CheckBox>
          </li>
          <template v-for="(table, index) in allTables">
            <li v-if="!type || !reservedTableById[table.id]" :key="index" class="table">
              <CheckBox :value="table.id" v-model="selectedIds" :disabled="disabledTableIds.includes(table.id)">
                <div class="inner">
                  <div class="attr">
                    <span class="name">{{ table.name }}</span>
                    <span>{{ availableSeat(table) }}</span>
                    <NoSmokeIcon v-if="table.smokingType === 1" width="16" height="16" class="nosmoke-icon" circle />
                    <SyncIcon v-if="!!table.syncGnaviId" width="16" height="16" class="sync-icon" />
                  </div>
                  <!-- TODO: CheckBoxのslot化する -->
                  <ul v-if="getDuplicatedReserves(table.id).length">
                    <li v-for="reserve in getDuplicatedReserves(table.id)" :key="reserve.id" class="reserve">
                      <span>{{ getReserveTime(reserve) }}</span>
                      <span class="name">{{ reserveName(reserve) }}</span>
                      <span>様</span>
                      <span>{{ reserveNumDisplay(reserve, (short = true)) }}</span>
                    </li>
                  </ul>
                </div>
                <AlertIcon v-if="isDuplicated(table.id)" width="24" height="24" class="alert-icon" />
              </CheckBox>
            </li>
          </template>
        </ul>
        <!-- TODO: slot化したい -->
        <ul v-if="deficiency || options.length || isTemporarySeat || noSelectedSeat" class="alert">
          <li v-if="options.length">
            <span>[{{ options }}]</span>
            指定のご予約です
          </li>
          <li v-else-if="isTemporarySeat">
            他社取り込み仮配席のご予約です
          </li>
          <li v-if="deficiency">
            <em> {{ deficiency }} </em>席<span>不足しています </span>
          </li>
          <li v-if="noSelectedSeat" class="block">
            <p>ブロックはテーブルの選択が必要です</p>
            テーブル未選択
          </li>
        </ul>
      </div>
    </template>
    <template #footer-center>
      <Button @click="onClose">キャンセル</Button>
      <Popover
        class="stafflist"
        placement="top-start"
        :open="isShowStaffList"
        :disabled="!isShowStaffList"
        @auto-hide="isShowStaffList = false"
      >
        <StaffSelect slot="popover" :staff-list="staffList" @select="selectStaff" />
        <Button primary @click="confirm" :disabled="buttonDisabled">選択</Button>
      </Popover>
      <ResrakuMessageModal
        v-if="resrakuMessageModal"
        @close="resrakuMessageModal = false"
        :isResrakuReserve="isResrakuReserve"
        :selectedIds="selectedIds"
        :backTo="backTo"
      />
    </template>
  </Modal>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { mapValues, without } from 'lodash'
import { toHourClock } from '@/utils/date'
import {
  reserveFooterAlert,
  reserveName,
  reserveNumDisplay,
  isTimeOverlap,
  unMatchedTableTypes,
  isResrakuReserve,
} from '@/utils/reserve'
import { isResrakuAlignment } from '@/utils/store'
import NoSmokeIcon from '@/components/icons/NoSmokeIcon'
import SyncIcon from '@/components/icons/sync'
import AlertIcon from '@/components/icons/AlertIcon'
import SelectBox from '@/components/atoms/SelectBox'
import Button from '@/components/atoms/Button'
import CheckBox from '@/components/atoms/CheckBox'
import Popover from '@/components/molecules/Popover'
import StaffSelect from '@/components/molecules/StaffSelect'
import Modal from '@/components/molecules/Modal'
import Dialog from '@/components/molecules/ModalDialog'
import ResrakuMessageModal from '@/components/organisms/ResrakuMessageModal'
import typeRoomMap from '@/resources/reservation/typeRoomMap'
import typeTableMap from '@/resources/reservation/typeTableMap'
import typeSmokingMap from '@/resources/reservation/typeSmokingMap'
import { isGnavi } from '@/resources/reservation/syncTypeMap'

export default {
  name: 'TableSelectModal',
  components: {
    NoSmokeIcon,
    SyncIcon,
    AlertIcon,
    SelectBox,
    Button,
    CheckBox,
    Popover,
    StaffSelect,
    Modal,
    ResrakuMessageModal,
  },
  props: {
    id: Number,
    reserveDate: String,
    reserve: Object,
    reservesByTableId: Object,
    getDeficiency: Function,
    getDuplicatedReserves: Function,
    isTemporarySeat: { type: Boolean, default: false },
    isBlock: { type: Boolean, default: false },
    hasStaffList: { type: Boolean, default: false },
    backTo: String,
  },
  data() {
    return {
      type: this.reserve.tableIds.length ? 0 : 1,
      table: this.reserve.tableIds.length ? this.reserve.tableIds : [],
      isShowStaffList: false,
      resrakuMessageModal: false,
    }
  },
  computed: {
    ...mapState({
      tables: state => state.tables,
      syncGnavis: state => state.syncGnavis,
      currentStore: state => state.stores.current.store,
      busy: state => state.tables.busy,
    }),
    ...mapGetters({
      allTables: 'tables/all',
      staffList: 'staffs/all',
    }),
    selectedType: {
      get() {
        return this.type
      },
      async set(value) {
        this.type = value
        if (value === 1 && this.hasDuplicate) {
          if (
            await Dialog.confirm({
              title: 'テーブル選択',
              message: 'テーブル選択を解除します。\nよろしいですか？',
              okText: '解除',
              cancelText: '戻る',
            })
          ) {
            this.selectedIds = []
          } else {
            this.type = 0
          }
        }
      },
    },
    selectedIds: {
      get() {
        return this.table
      },
      set(value) {
        this.table = value[value.length - 1] === -1 ? [-1] : without(value, -1)
      },
    },
    reservedTableById() {
      return mapValues(this.reservesByTableId, reserves =>
        reserves.some(reserve => isTimeOverlap(reserve, this.reserve)),
      )
    },
    options() {
      const { tableTypeLabel, tableType, smokingType, privRoomType } = this.reserve
      return reserveFooterAlert(
        [
          tableTypeLabel || (tableType && typeTableMap[tableType]),
          smokingType && typeSmokingMap[smokingType],
          privRoomType && typeRoomMap[privRoomType],
        ],
        14,
      )
    },
    deficiency() {
      return this.getDeficiency(this.selectedIds)
    },
    disabledTableIds() {
      const selectedTableIds = (this.selectedIds || []).filter(id => id === -1 || this.tables.byId[id])
      if (
        !selectedTableIds.length ||
        this.currentStore.syncStatus.gnavi !== 2 ||
        !isGnavi(this.reserve.syncType) ||
        selectedTableIds.includes(-1)
      ) {
        return []
      }
      const selectedTables = selectedTableIds.map(id => this.tables.byId[id] || {})
      const selectedTable = (selectedTables.length && selectedTables[0]) || {}

      return this.tables.allIds.filter(id => this.tables.byId[id].syncGnaviId !== selectedTable.syncGnaviId)
    },
    hasDuplicate() {
      return this.selectedIds.filter(id => id !== -1 && this.getDuplicatedReserves(id).length).length
    },
    unMatchedTableOptions() {
      const rsv = { ...this.reserve, ...{ tableIds: this.selectedIds } }
      return unMatchedTableTypes(rsv, this.tables, this.syncGnavis)
    },
    noSelectedSeat() {
      return !this.selectedIds.length && this.isBlock
    },
    isResrakuReserve() {
      return isResrakuReserve(this.reserve)
    },
    buttonDisabled() {
      return (
        (this.isResrakuReserve?.hotpepper || this.isResrakuReserve?.inquiry) &&
        (!this.selectedIds.length || this.selectedIds.includes(-1))
      )
    },
    isResrakuAlignment() {
      return isResrakuAlignment(this.currentStore)
    },
  },
  async mounted() {
    const { dispatch } = this.$store
    dispatch('syncGnavis/get', { readonly: true })
    dispatch('tables/get', { readonly: true })
    await dispatch('reserves/get', { date: this.reserveDate, readonly: true })
  },
  methods: {
    reserveName,
    reserveNumDisplay,
    availableSeat(item) {
      const { minAvailableSeat: min, maxAvailableSeat: max } = item
      return `(${min === max ? min : `${min} - ${max}`}名)`
    },
    getReserveTime(reserve) {
      return `${toHourClock(reserve.reserveDatetime).time}〜`
    },
    isDuplicated(tableId) {
      return this.selectedIds.includes(tableId) && !!this.getDuplicatedReserves(tableId).length
    },
    async confirm() {
      if (this.hasDuplicate || this.deficiency || this.unMatchedTableOptions.length) {
        if (
          !(await Dialog.confirm({
            title: '先に進んでよろしいでしょうか？',
            message: [
              this.hasDuplicate ? '･予約が重複' : '',
              this.deficiency ? '･選択した席に対し予約人数がオーバー' : '',
              this.unMatchedTableOptions.length
                ? `・[ ${reserveFooterAlert(this.unMatchedTableOptions)} ] 指定のご予約です`
                : '',
            ]
              .filter(v => v)
              .join('\n'),
          }))
        ) {
          return
        }
      }
      if (this.isResrakuReserve?.hotpepper && this.isResrakuAlignment) {
        this.resrakuMessageModal = true
        return
      }
      if (this.hasStaffList && this.staffList.length > 0) {
        this.staffList.length === 1 ? this.selectStaff(this.staffList[0]) : (this.isShowStaffList = true)
      } else {
        this.onClose()
        this.$emit('select', this.selectedIds)
      }
    },
    onClose() {
      this.$emit('close')
    },
    async selectStaff(staff) {
      this.isShowStaffList = false
      if ((this.isResrakuReserve?.inquiry || this.isResrakuReserve?.retty) && this.isResrakuAlignment) {
        await this.$store.dispatch('resraku/request/accept', {
          rsvId: this.reserve.id,
          staffId: staff.id,
          tableList: this.selectedIds.sort().join(),
        })
        location.assign(`/sp/reserve/${this.reserve.id}`)
        return
      }
      this.$emit('select', this.selectedIds)
      const id = await this.$store.dispatch('reserves/edit/save', staff.id)
      this.$router.push({ path: `/sp/reserve/${id}` })
    },
  },
}
</script>

<style lang="postcss" scoped>
.table-select {
  display: grid;
  grid-auto-flow: row;
  grid-template-rows: auto 1fr;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.type {
  padding: 16px;

  .select-box {
    width: 100%;
  }
}

.table-list {
  overflow: auto;
}

.table {
  .check-box {
    box-sizing: border-box;
    display: grid;
    grid-template-columns: auto 1fr;
    padding: 16px;
    width: 100%;
    &::before {
      border-bottom: solid 1px var(--very-light-gray-3);
      content: '';
      display: block;
      left: 0;
      margin: 0 16px;
      position: absolute;
      right: 0;
    }
    >>> .box {
      align-self: center;
    }
    >>> .label {
      align-items: center;
      display: grid;
      grid-gap: 16px;
      grid-template-columns: 1fr auto;
    }
  }
}

.inner {
  align-items: center;
  display: grid;
  grid-auto-flow: row;
  grid-gap: 8px;
  line-height: normal;
  padding: 0;
}

.attr {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  line-height: normal;
  padding: 0;
  width: fit-content;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reserve {
  align-items: center;
  display: grid;
  font-size: 13px;
  grid-auto-flow: column;
  grid-gap: 4px;
  width: fit-content;
}

.nosmoke-icon {
  fill: var(--vivid-pink);
}

.sync-icon {
  fill: var(--very-dark-gray-2);
}

.alert-icon {
  fill: var(--moderate-blue);
}

.alert {
  background-color: var(--vivid-pink);
  bottom: 0;
  color: var(--white);
  display: grid;
  font-size: 15px;
  font-weight: bold;
  grid-auto-flow: row;
  grid-gap: 8px;
  left: 0;
  line-height: 1;
  opacity: 0.8;
  padding: 8px 0;
  position: sticky;
  right: 0;
  text-align: center;
  z-index: 1000;

  em {
    font-size: 24px;
    font-style: normal;
  }

  .block {
    font-weight: 600;
    p {
      margin-bottom: 8px;
    }
  }
}

.type-info {
  display: inline-block;
  margin-right: 8px;
  position: relative;
  & + &::before {
    content: '･';
    display: inline-block;
    left: -8px;
    position: absolute;
  }

  &:last-child {
    margin-right: 0;
  }
}
</style>
